import { Button } from '@customerio/pluma-components/react';
import type { ButtonProps } from '@customerio/pluma-components/react';
import { forwardRef } from 'react';
import type { ReactNode } from 'react';
import googleLogo from '@/assets/images/login-google-logo.svg';
import * as styles from './google-auth-button.css';

type GoogleAuthButtonProps = {
  type?: 'login' | 'signup';
  /** Light (default) matches the login page; dark matches the re-auth modal (Ember's `@theme='dark'`). */
  theme?: 'light' | 'dark';
  /**
   * Overrides the default Google copy. Only for contexts where the surrounding
   * sentence already sets up the action (the SSO settings panel's "Sign in with
   * your Google account"); leave unset on sign-in surfaces so the branded copy stands.
   */
  label?: ReactNode;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
  'data-testid'?: string;
  /** Remaining props (e.g. a custom Tooltip's `getReferenceProps()`) reach the button. */
} & Omit<ButtonProps, 'type' | 'variant' | 'className' | 'iconSrc' | 'children' | 'onClick'>;

const LOGIN_STYLE = { light: styles.googleAuthButton, dark: styles.googleAuthButtonDark } as const;

/**
 * "Sign in with Google" button. Copy + mark follow Google's branding guidelines.
 * Forwards its ref so it can be the trigger of a custom Pluma tooltip.
 */
export const GoogleAuthButton = forwardRef<HTMLButtonElement, GoogleAuthButtonProps>(function GoogleAuthButton(
  { type = 'login', theme = 'light', label, isDisabled, isLoading, onClick, 'data-testid': dataTestId, ...restProps },
  ref,
) {
  const defaultLabel = type === 'login' ? 'Sign in with Google' : 'Continue with Google';

  return (
    <Button
      ref={ref}
      // Explicit button type so clicking it (to open the Google popup) never
      // implicitly submits a surrounding <Form>.
      type="button"
      variant="secondary"
      // Google brand styling (white/dark surface for login, full-width for
      // signup) — see the `google*Button` styles.
      className={type === 'login' ? LOGIN_STYLE[theme] : styles.googleSignupButton}
      iconSrc={googleLogo}
      isDisabled={isDisabled}
      isLoading={isLoading}
      onClick={onClick}
      data-testid={dataTestId}
      {...restProps}
    >
      {label ?? defaultLabel}
    </Button>
  );
});
